import { AnimatePresence, motion } from 'framer-motion'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { AiFillSafetyCertificate, AiOutlineExclamationCircle, AiTwotoneLock, AiTwotonePhone } from 'react-icons/ai'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { getSmsCode, resetPwd } from '@/api/login'
import { loginStore, useRecoilState, useRecoilValue, useSetRecoilState } from '@/store'

import { getMobileMaxLength, getMobileValidateStatus, isMobileValid, normalizeMobileInput } from '../../utils'
import Area from '../Area'
import Slider from '../Slider'
const { stepState, areaState, mobileState, needSliderState } = loginStore

export default function Index() {
  const setStep = useSetRecoilState(stepState)
  const [areaCode, setAreaCode] = useRecoilState(areaState)
  const [mobile, setMobile] = useRecoilState(mobileState)
  const [mobileError, setMobileError] = useState('')
  const [input, setInput] = useState('')
  const [codeError, setCodeError] = useState('')
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [repwd, setRepwd] = useState('')
  const [repwdError, setRepwdError] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [countdownText, setCountdownText] = useState(60)
  const needSlider = useRecoilValue(needSliderState) // 是否需要滑块验证
  const countdownRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined)
  const navigate = useNavigate()

  const handleSubmit = useMutation(() => {
    return resetPwd({
      area: areaCode,
      mobile,
      code: input,
      password: password,
      repwd: repwd
    })
  })

  useEffect(() => {
    if (handleSubmit.status === 'error') {
      setErrorMsg('重置密码失败，请重试')
    }
    if (handleSubmit.data) {
      if (handleSubmit.data.status !== 0) {
        setErrorMsg(handleSubmit.data.msg)
      } else {
        if (handleSubmit.status === 'success') {
          toast.success('重置密码成功')
          setStep(1)
        }
      }
    }
  }, [handleSubmit.status])

  const smsCode = useMutation(({ sessionId, sig, token }: { sessionId: string | undefined; sig: string | undefined; token: string | undefined }) => {
    return getSmsCode({
      areaCode,
      mobile,
      sessionId,
      sign: sig,
      scene: 'nc_login',
      token,
      type: 'FORGET'
    })
  })

  const countdown = () => {
    countdownRef.current = setInterval(() => {
      setCountdownText((v) => {
        const end = v - 1
        if (end === 0) {
          clearInterval(countdownRef.current)
          return 60
        } else {
          return end
        }
      })
    }, 1000)
  }

  useEffect(() => {
    if (smsCode.status === 'error') {
      setErrorMsg('获取验证码失败，请重试')
      needSlider && window.nc.reset()
    }
    if (smsCode.data && smsCode.data.status !== 0) {
      setErrorMsg(smsCode.data.msg)
      needSlider && window.nc.reset()
    } else {
      if (smsCode.status === 'success') {
        toast.success(smsCode.data.data)
        countdown()
      }
    }
  }, [smsCode.status])

  const changeAreaCode = (nextArea: string) => {
    setAreaCode(nextArea)
    setMobile('')
    setMobileError('')
    setErrorMsg('')
  }

  const inputChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const val = e.target.value
    if (type === 'mobile') {
      setMobile(normalizeMobileInput(val, areaCode))
    } else if (type === 'input') {
      setInput(val)
    } else if (type === 'password') {
      setPassword(val)
    } else {
      setRepwd(val)
    }
  }

  useEffect(() => {
    const status = getMobileValidateStatus(mobile, areaCode)
    setMobileError(status)
    setErrorMsg(status === 'error' ? '请输入正确的手机号' : '')
  }, [mobile, areaCode])

  useEffect(() => {
    if (input.length === 6) {
      setCodeError('success')
      setErrorMsg('')
    } else {
      setCodeError('')
      setErrorMsg('')
    }
  }, [input])

  useEffect(() => {
    if (password.length >= 3) {
      if (/^[\w]{6,20}$/.test(password)) {
        setPasswordError('success')
        setErrorMsg('')
      } else {
        setPasswordError('error')
        setErrorMsg('密码格式不正确(6-20位字母或数字)')
      }
    } else {
      setPasswordError('')
      setErrorMsg('')
    }
  }, [password])

  useEffect(() => {
    if (repwd.length >= 3) {
      if (password === repwd) {
        setRepwdError('success')
        setErrorMsg('')
      } else {
        setRepwdError('error')
        setErrorMsg('两次输入密码不一致')
      }
    } else {
      setRepwdError('')
      setErrorMsg('')
    }
  }, [repwd])

  const canGetSms = countdownText === 60 && isMobileValid(mobile, areaCode)

  const ncSign = (data) => {
    if (!isMobileValid(mobile, areaCode)) return
    setErrorMsg('')
    smsCode.mutate(data)
  }

  const resetNc = () => {
    if (!canGetSms) return
    // 不需滑块
    if (!needSlider) {
      smsCode.mutate({
        sessionId: undefined,
        sig: undefined,
        token: undefined
      })
      return
    }
    // 需滑块
    setErrorMsg('请先进行滑动验证')
    window.nc.reset()
  }

  return (
    <Container className="h-full w-full flex flex-col items-start justify-center">
      <div className="text-[24px] text-[#333]">重置密码</div>
      <div className="w-[350px] my-[30px]">
        <div
          className={`w-full h-[40px] border-b border-[#ddd] flex items-center justify-center mb-[30px] ${
            mobileError === 'success' ? 'border-[#36c236]' : ''
          } ${mobileError === 'error' ? 'border-[red]' : ''}`}
        >
          <div className="w-[40px] h-full flex items-center justify-center">
            <AiTwotonePhone className="text-[28px] text-[#999]" />
          </div>
          <Area onChange={changeAreaCode} areaCode={areaCode} />
          <div className="flex-1 h-full relative">
            <input
              value={mobile}
              onChange={(e) => inputChange(e, 'mobile')}
              className="absolute h-full w-full p-[10px] border-0 outline-none text-[20px] text-[#656565]"
              placeholder="请输入手机号码"
              maxLength={getMobileMaxLength(areaCode)}
            ></input>
          </div>
        </div>
        <div
          className={`w-full h-[40px] border-b border-[#ddd] flex items-center justify-center mb-[30px] ${
            codeError === 'success' ? 'border-[#36c236]' : ''
          } ${codeError === 'error' ? 'border-[red]' : ''}
        `}
        >
          <div className="w-[40px] h-full flex items-center justify-center">
            <AiFillSafetyCertificate className="text-[28px] text-[#999]" />
          </div>
          <div className="flex-1 h-full relative">
            <input
              value={input}
              onChange={(e) => inputChange(e, 'input')}
              className="absolute h-full w-full p-[10px] border-0 outline-none text-[20px] text-[#656565]"
              placeholder="请输入验证码"
              maxLength={6}
            ></input>
          </div>
          <div className="w-[120px] h-full flex items-center justify-center">
            <button
              disabled={!canGetSms}
              onClick={resetNc}
              className={`btn-def w-full h-[36px] rounded-full flex items-center justify-center outline-none text-[16px] text-[#FFF]
              ${canGetSms ? 'btn-active' : ''}
            `}
            >
              {countdownText !== 60 ? `重新获取${countdownText}s` : '获取验证码'}
            </button>
          </div>
        </div>
        {needSlider ? (
          <div className={`h-[34px] mb-[30px] ${mobileError === 'success' ? '' : 'hidden'}`}>
            <Slider ncSign={ncSign} />
          </div>
        ) : null}
        <div
          className={`w-full h-[40px] border-b border-[#ddd] flex items-center justify-center mb-[30px] ${
            passwordError === 'success' ? 'border-[#36c236]' : ''
          } ${passwordError === 'error' ? 'border-[red]' : ''}
        `}
        >
          <div className="w-[40px] h-full flex items-center justify-center">
            <AiTwotoneLock className="text-[28px] text-[#999]" />
          </div>
          <div className="flex-1 h-full relative">
            <input
              value={password}
              type="password"
              onChange={(e) => inputChange(e, 'password')}
              className="absolute h-full w-full p-[10px] border-0 focus:ring-transparent text-[20px] text-[#656565]"
              placeholder="请输入新密码"
            ></input>
          </div>
        </div>
        <div
          className={`w-full h-[40px] border-b border-[#ddd] flex items-center justify-center ${repwdError === 'success' ? 'border-[#36c236]' : ''} ${repwdError === 'error' ? 'border-[red]' : ''}
        `}
        >
          <div className="w-[40px] h-full flex items-center justify-center">
            <AiTwotoneLock className="text-[28px] text-[#999]" />
          </div>
          <div className="flex-1 h-full relative">
            <input
              value={repwd}
              type="password"
              onChange={(e) => inputChange(e, 'repwd')}
              className="absolute h-full w-full p-[10px] border-0 focus:ring-transparent text-[20px] text-[#656565]"
              placeholder="请再次输入新密码"
            ></input>
          </div>
        </div>
      </div>
      {errorMsg ? (
        <div className="text-[#e82925] text-[14px] flex items-center mb-[20px]">
          <AiOutlineExclamationCircle className="mr-[5px]" />
          {errorMsg}
        </div>
      ) : null}
      <div className="w-[350px]">
        <div className="w-full mb-[20px] flex items-center justify-between">
          <div className="cursor-pointer text-[14px] text-[#333333]" onClick={() => setStep(1)}>
            返回登录
          </div>
        </div>
        <div className="w-full">
          <button
            disabled={!(codeError === 'success' && mobileError === 'success' && passwordError === 'success' && repwdError === 'success')}
            onClick={() => handleSubmit.mutate()}
            className={`btn-def w-[260px] h-[48px] rounded-full flex items-center justify-center outline-none text-[20px] text-[#FFF]
            ${codeError === 'success' && mobileError === 'success' && passwordError === 'success' && repwdError === 'success' ? 'btn-active' : ''}
            `}
          >
            提交
          </button>
        </div>
      </div>
    </Container>
  )
}

const Container = styled(motion.div)`
  .btn-def {
    background: linear-gradient(270deg, #888, #ccc);
  }
  .btn-active {
    background: linear-gradient(90deg, #ff7839, #fe4844);
  }
`
